import { useCallback } from 'react';
import { useMutation } from 'react-query';

import { useAuthHeaders } from 'auth/hooks';
import { llmService } from 'chatbot/services';
import {
  ChatbotRequestType,
  IChatbotAnswersRequest,
  IChatbotMessageRequest,
  IChatbotRequest,
  IChatbotResponse,
} from 'chatbot/types';
import { NotificationPosition } from 'core/enums';
import { useHandleErrors } from 'core/hooks';

export const useLlmActions = ({
  notificationPosition = NotificationPosition.DRAWER,
}: {
  notificationPosition?: NotificationPosition;
} = {}) => {
  return {
    ...useStartChatbot(notificationPosition),
    ...useSendChatbotMessage(notificationPosition),
    ...useSendChatbotAnswers(notificationPosition),
  };
};

const useStartChatbot = (notificationPosition: NotificationPosition) => {
  const headers = useAuthHeaders();

  const { errors } = useHandleErrors({
    position: notificationPosition,
  });

  const service = useCallback(
    () => llmService.startChatbot({ requestOptions: headers }),
    [headers]
  );

  const { mutateAsync } = useMutation(service, {
    ...errors,
  });

  const startChatbot = (): Promise<{ threadId: string }> => mutateAsync({});

  return { startChatbot };
};

const useSendChatbotRequest = (notificationPosition: NotificationPosition) => {
  const headers = useAuthHeaders();

  const { errors } = useHandleErrors({
    position: notificationPosition,
  });

  const service = useCallback(
    (payload: IChatbotRequest) => {
      return llmService.sendChatbotRequest(payload, {
        requestOptions: headers,
        toSnakeCase: (key: string) => key,
      });
    },
    [headers]
  );

  const { mutateAsync } = useMutation<IChatbotResponse, Error, IChatbotRequest>(service, {
    ...errors,
  });

  const sendChatbotRequest = useCallback(
    (payload: IChatbotRequest) => mutateAsync(payload),
    [mutateAsync]
  );

  return { sendChatbotRequest };
};

const useSendChatbotMessage = (notificationPosition: NotificationPosition) => {
  const { sendChatbotRequest } = useSendChatbotRequest(notificationPosition);

  const sendMessage = useCallback(
    (payload: Omit<IChatbotMessageRequest, 'type'>) =>
      sendChatbotRequest({ ...payload, type: ChatbotRequestType.MESSAGE }),
    [sendChatbotRequest]
  );

  return { sendMessage };
};

const useSendChatbotAnswers = (notificationPosition: NotificationPosition) => {
  const { sendChatbotRequest } = useSendChatbotRequest(notificationPosition);

  const sendAnswers = useCallback(
    (payload: Omit<IChatbotAnswersRequest, 'type'>) =>
      sendChatbotRequest({ ...payload, type: ChatbotRequestType.ANSWERS }),
    [sendChatbotRequest]
  );

  return { sendAnswers };
};
